import { inject, Injectable } from "@angular/core";
import { map, Observable, of } from "rxjs";
import { ChangeProposalStatusGQL, ChangeProposalStatusMutation, ComparatorOperators, CreateAuditPlanGQL, CreateAuditPlanMutation, CreateProposalGQL, CreateProposalMutation, CreateProposalRequest, DeleteProposalGQL, DeleteProposalMutation, DownloadProposalsGQL, DownloadProposalsImportTemplateGQL, DownloadProposalsImportTemplateMutation, DownloadProposalsMutation, GetProposalFiltersGQL, GetProposalFiltersQuery, GetProposalsAuditEntitiesGQL, GetProposalsAuditEntitiesQuery, GetProposalsByIdGQL, GetProposalsByIdQuery, GetProposalsGQL, GetProposalsIdsGQL, GetProposalsIdsQuery, GetProposalsOptionsGQL, GetProposalsOptionsQuery, GetProposalsQuery, GetRecommendedAuditSubAreasGQL, GetRecommendedAuditSubAreasQuery, OperatorInput, PagingInput, ProposalColumns, ProposalQueryComplexInput, ProposalQueryInput, UpdateProposalGQL, UpdateProposalMutation, UpdateProposalRequest, UploadProposalGQL, UploadProposalMutation } from "../../../graphql/generated";
import { ProposalStatus } from '../../../graphql/generated';

@Injectable({
    providedIn: 'root'
})

export class ProposalService {
    private readonly GetProposalsOptionsGQL: GetProposalsOptionsGQL = inject(GetProposalsOptionsGQL);
    private readonly GetProposalsGQL: GetProposalsGQL = inject(GetProposalsGQL);
    private readonly GetProposalsIdsGQL: GetProposalsIdsGQL = inject(GetProposalsIdsGQL);
    private readonly CreateProposalGQL: CreateProposalGQL = inject(CreateProposalGQL);
    private readonly UpdateProposalGQL: UpdateProposalGQL = inject(UpdateProposalGQL);
    private readonly DeleteProposalGQL: DeleteProposalGQL = inject(DeleteProposalGQL);
    private readonly CreateAuditPlanGQL: CreateAuditPlanGQL = inject(CreateAuditPlanGQL);
    private readonly DownloadProposalsGQL: DownloadProposalsGQL = inject(DownloadProposalsGQL)
    private readonly ChangeProposalStatusGQL: ChangeProposalStatusGQL = inject(ChangeProposalStatusGQL);
    private readonly GetProposalsAuditEntitiesGQL: GetProposalsAuditEntitiesGQL = inject(GetProposalsAuditEntitiesGQL);
    private readonly UploadProposalGQL: UploadProposalGQL = inject(UploadProposalGQL);
    private readonly DownloadProposalsImportTemplateGQL: DownloadProposalsImportTemplateGQL = inject(DownloadProposalsImportTemplateGQL);
    private readonly GetProposalFiltersGQL: GetProposalFiltersGQL = inject(GetProposalFiltersGQL);
    private readonly GetProposalsByIdGQL: GetProposalsByIdGQL = inject(GetProposalsByIdGQL);
    private readonly GetRecommendedAuditSubAreasGQL: GetRecommendedAuditSubAreasGQL = inject(GetRecommendedAuditSubAreasGQL);

    loadProposal(proposalIds: string[], parameters: any, year: number, ids: number[], legalEntityIds: string[]): Observable<GetProposalsQuery['getProposals']> {
        const param = convertLazyLoadToComplexWithPage(parameters);
        const legalEntityId = legalEntityIds.map(le => Number(le))
        return this.GetProposalsGQL.fetch({ page: param.page, query: param.query, request: proposalIds, year, ids, legalEntityId }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.getProposals))
    }

    loadProposalIds(proposalIds: string[], parameters: any, year: number, ids: number[], legalEntityIds: string[]): Observable<GetProposalsIdsQuery['getProposals']> {
        const param = convertLazyLoadToComplexWithPage(parameters);
        const legalEntityId = legalEntityIds.map(le => Number(le))
        return this.GetProposalsIdsGQL.fetch({ page: { pageNo: 0, pageSize: 1000000 }, query: param.query, request: proposalIds, year, ids, legalEntityId }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.getProposals))
    }

    loadProposalOptions(year: number): Observable<GetProposalsOptionsQuery['getProposalsOptions']> {
        // return of(MOCK_PROPOSAL).pipe(delay(1000))
        return this.GetProposalsOptionsGQL.fetch({ year }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.getProposalsOptions))
    }

    createProposal(request: CreateProposalRequest): Observable<CreateProposalMutation['createProposal']> {
        return this.CreateProposalGQL.mutate({ request }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.createProposal))
    }

    updateProposal(request: UpdateProposalRequest): Observable<UpdateProposalMutation['updateProposal']> {
        return this.UpdateProposalGQL.mutate({ request }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateProposal))
    }

    deleteProposal(id: number): Observable<DeleteProposalMutation['deleteProposal']> {
        return this.DeleteProposalGQL.mutate({ id }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.deleteProposal))
    }

    createAuditPlan(name: string, legalEntityId: number, presentationDate: string | null, year: number): Observable<CreateAuditPlanMutation['createAuditPlan']> {
        return this.CreateAuditPlanGQL.mutate({ request: { legalEntityId, name, year, presentationDate } }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.createAuditPlan))
    }

    downloadProposals(proposalIds: string[], parameters: any, year: number, timeZone: string): Observable<DownloadProposalsMutation['downloadProposals']> {
        const param = convertLazyLoadToComplexWithPage(parameters);
        return this.DownloadProposalsGQL.mutate({ query: param.query, request: proposalIds, year, timeZone }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.downloadProposals))
    }

    changeProposalStatus(id: number, status: ProposalStatus): Observable<ChangeProposalStatusMutation['changeProposalStatus']> {
        return this.ChangeProposalStatusGQL.mutate(
            { id, status },
            { fetchPolicy: 'no-cache' }
        ).pipe(
            map(res => res.data?.changeProposalStatus)
        );
    }

    getProposalsAuditEntities(ids: number[]): Observable<GetProposalsAuditEntitiesQuery['getProposalsAuditEntities']> {
        return this.GetProposalsAuditEntitiesGQL.fetch({ ids }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.getProposalsAuditEntities))
    }

    uploadProposal(base64: string): Observable<UploadProposalMutation['uploadProposals']> {
        return this.UploadProposalGQL.mutate({ base64 }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.uploadProposals as UploadProposalMutation['uploadProposals']))
    }

    downloadProposalTemplate(): Observable<DownloadProposalsImportTemplateMutation['downloadProposalsImportTemplate']> {
        return this.DownloadProposalsImportTemplateGQL.mutate({}, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.downloadProposalsImportTemplate))
    }

    getProposalFilters(legalEntityId: string[], proposalIds: number[], year: number): Observable<GetProposalFiltersQuery['proposalFieldsValues']> {
        const proposalId = proposalIds.map(id => String(id));
        return this.GetProposalFiltersGQL.fetch({ legalEntityId, proposalId, year }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.proposalFieldsValues))
    }

    getProposalsById(ids: number[]): Observable<GetProposalsByIdQuery['getProposalsByIds']> {
        const idsProposals: string[] = ids.map(id => String(id));
        return this.GetProposalsByIdGQL.fetch({ ids: idsProposals }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.getProposalsByIds))
    }

    getRecommendedAuditSubAreas(auditEntityIds: number[]): Observable<GetRecommendedAuditSubAreasQuery['getRecommendedAuditSubAreas']> {
        return this.GetRecommendedAuditSubAreasGQL.fetch({ auditEntityIds: auditEntityIds.map(String) }, { fetchPolicy: 'no-cache' }).pipe(
            map(res => res.data.getRecommendedAuditSubAreas)
        );
    }

}

export function convertLazyLoadToComplexWithPage(parameters: any): { query: ProposalQueryComplexInput, page: PagingInput } {
    const query: ProposalQueryInput[] = [];
    if (parameters) {

        if (parameters.filters) {

            Object.entries(parameters.filters).forEach(([field, values]) => {

                if (values !== null && Array.isArray(values) && values.length > 0) {
                    (ENUM_PROPOSAL[field as keyof typeof ENUM_PROPOSAL])?.forEach(key => {

                        query.push({
                            field: key as ProposalColumns,
                            filters: {
                                operator: key === ProposalColumns.ProposalPlanningManDays || key === ProposalColumns.AuditPlanYear ? ComparatorOperators.Eq : ComparatorOperators.Match,
                                value:
                                    key === ProposalColumns.LegalEntityCode ? values.map(v => v?.split(/\s-\s(.+)/)[0]?.trim()) :
                                        key === ProposalColumns.ProposalApc ? values.map(v => v.split(' - ').slice(0, 2)?.join('-')) :
                                            key === ProposalColumns.ProposalApcProcess || key === ProposalColumns.ProposalApcLegalEntity ? values.map(v => v?.split(/\s-\s(.+)/)[0]?.trim()) :
                                                values
                            }
                        });
                    })
                }
            });
        }
    }
    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query: query,
                    operator: OperatorInput.And
                }
            ]
        },
        page: {
            pageNo: parameters?.page ?? 0,
            pageSize: parameters?.rows ?? 1000000
        }
    }
}

export const ENUM_PROPOSAL = {
    auditPlanYear: [ProposalColumns.AuditPlanYear],
    auditPlan: [ProposalColumns.AuditPlanName],
    businessUnit: [ProposalColumns.BusinessUnit],
    organizationalUnit: [ProposalColumns.OrganizationalUnit],
    boardLegalEntity: [ProposalColumns.LegalEntityCode],
    auditArea: [ProposalColumns.ProposalAuditArea],
    auditSubArea: [ProposalColumns.ProposalAuditSubarea],
    smartCode: [ProposalColumns.ProposalSmartCode],
    name: [ProposalColumns.ProposalName],
    sensitiveAudit: [ProposalColumns.ProposalSensitiveAudit],
    auditEntities: [ProposalColumns.ProposalApc],
    processCoverages: [ProposalColumns.ProposalApcProcess],
    legalEntityCovered: [ProposalColumns.ProposalApcLegalEntity],
    typeOfAudit: [ProposalColumns.ProposalTypeOfAudit],
    minitorRationale: [ProposalColumns.ProposalMinitorRationale],
    minitorScope: [ProposalColumns.ProposalMinitorScope],
    minitorKeyRisks: [ProposalColumns.ProposalMinitorKeyRisks],
    thematicAuditScope: [ProposalColumns.ProposalThematicAuditScope],
    consolidatedThematicAudit: [ProposalColumns.ProposalConsolidatedThematicAudit],
    localThematicAudit: [ProposalColumns.ProposalLocalThematicAudit],
    localCountriesParticipants: [ProposalColumns.ProposalLocalCountriesPartecipants],
    expectedStartDate: [ProposalColumns.ProposalExpectedStartingDate],
    expectedStartingDate: [ProposalColumns.ProposalExpectedStartingDate],
    expectedEndDate: [ProposalColumns.ProposalExpectedDeliveryDate],
    expectedDeliveryDate: [ProposalColumns.ProposalExpectedDeliveryDate],
    expectedDeliveryQuarter: [ProposalColumns.ProposalExpectedDeliveryQuarter],
    plannedManDays: [ProposalColumns.ProposalPlanningManDays],
    applicableGroupPolicies: [ProposalColumns.ProposalApplicableGroupPolicies],
    applicableGroupGuidelines: [ProposalColumns.ProposalApplicableGroupGuidelines],
    agAuditPlan: [ProposalColumns.ProposalAgAuditPlan],
    ghoAuditType: [ProposalColumns.ProposalGhoAuditType],
    gblType: [ProposalColumns.ProposalGblType],
    btaType: [ProposalColumns.ProposalBtaType],
    btaSupportOnAuditAgreed: [ProposalColumns.ProposalBtaSupportOnAuditAgreed],
    rationalBtaSupport: [ProposalColumns.ProposalRationaleSupportAudit],
    dataAnalyticsCoverage: [ProposalColumns.ProposalDataAnalyticsCoverage],
    applyOrExplain: [ProposalColumns.ProposalApplyOrExplain],
    sustainabilityPrioritiesCoverage: [ProposalColumns.ProposalSustainabilityPrioritiesCoverage],
    sustainabilityPriorities: [ProposalColumns.ProposalSustainabilityPriorities],
    impactValueAdded: [ProposalColumns.ProposalImpactValueAdded],
    enhancedApproach: [ProposalColumns.ProposalEnhancedApproach],
    mainGroupStrategyPillar: [ProposalColumns.ProposalMainGroupStrategy],
    status: [ProposalColumns.ProposalStatus],
    proposalAdded: [ProposalColumns.ProposalProposalAdded],
    note: [ProposalColumns.ProposalNotes],
    mainTopic: [ProposalColumns.ProposalMainTopic],
    auditType: [ProposalColumns.ProposalTypeOfAudit],
    expectedDeliveryQuarterDate: [ProposalColumns.ProposalExpectedDeliveryQuarter],
    dataAnalyticsComment: [ProposalColumns.ProposalDataAnalyticsOther]
}